/**
 * CP437 is the printer's default code page (the KPC307 self-test says
 * "Default Codepage: CP437(0)"). Text mode can print only characters of this
 * table; everything else is transliterated. Image mode prints ₹, Hindi,
 * Assamese and any other script.
 *
 * Moved from the proof of concept (`web/shared/cp437.ts`).
 */

const CP437_HIGH =
	"ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐" +
	"└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ";

const ENCODE = new Map<string, number>();
for (let i = 0; i < CP437_HIGH.length; i++) ENCODE.set(CP437_HIGH.charAt(i), 0x80 + i);

const REPLACE: Readonly<Record<string, string>> = {
	"₹": "Rs.",
	"€": "EUR",
	"‘": "'",
	"’": "'",
	"“": '"',
	"”": '"',
	"–": "-",
	"—": "-",
	"…": "...",
	"•": "*",
	"×": "x",
	"\t": " ",
};

/** Combining diacritical marks, U+0300 to U+036F. */
const COMBINING = /[̀-ͯ]/g;

/** A character the printer has in CP437: printable ASCII or one of the high characters. */
export function isCp437(ch: string): boolean {
	const c = ch.codePointAt(0) ?? 0;
	return (c >= 0x20 && c < 0x7f) || ENCODE.has(ch);
}

/** The CP437 text for one character, or null when it has none (it would print as `?`). */
export function transliterateChar(ch: string): string | null {
	if (isCp437(ch)) return ch;
	const replacement = REPLACE[ch];
	if (replacement !== undefined) return replacement;
	const base = ch.normalize("NFD").replace(COMBINING, "");
	return base !== ch && [...base].every(isCp437) ? base : null;
}

/** Maps a string onto characters that exist in CP437. Characters without one become `?`. */
export function transliterate(s: string): string {
	let out = "";
	for (const ch of s) out += transliterateChar(ch) ?? "?";
	return out;
}

/** Encodes an already transliterated string to CP437 bytes. */
export function encodeCp437(s: string): number[] {
	const out: number[] = [];
	for (const ch of s) {
		const c = ch.codePointAt(0) ?? 0x3f;
		out.push(c < 0x80 ? c : (ENCODE.get(ch) ?? 0x3f));
	}
	return out;
}
